import { UIChunkType } from '@myre/app-agent-domain';

import { Injectable } from '@angular/core';
import { isDataUIPart } from 'ai';
import { sumBy } from 'lodash-es';

import {
  type MyreUiMessage,
  type MyreUiMessagePart,
} from '../interfaces/chat.interfaces';

@Injectable({ providedIn: 'root' })
export class AgentStatusService {
  private readonly _textVisibilityThreshold = 50;

  /**
   * Determines whether to show the status indicator.
   * Shows only while streaming AND before substantial text content appears.
   */
  public shouldShowStatus(
    message: MyreUiMessage | null,
    isStreaming: boolean,
  ): boolean {
    if (!isStreaming || message?.role !== 'assistant') {
      return false;
    }

    // An interactive result (clarification question / CTA) means the agent has
    // finished thinking — hide the status/loader even if the stream is closing.
    if (this._hasInteractivePart(message)) {
      return false;
    }

    return this._getTextLength(message) < this._textVisibilityThreshold;
  }

  private _hasInteractivePart(message: MyreUiMessage): boolean {
    return message.parts.some(
      part =>
        isDataUIPart(part) &&
        (part.type === UIChunkType.DataQuestion ||
          part.type === UIChunkType.DataCta),
    );
  }

  /**
   * Extracts and cleans the status text from message parts.
   */
  public extractStatusText(message: MyreUiMessage | null): string | null {
    if (!message) {
      return null;
    }

    const rawStatus = this._extractRawStatusFromParts(message.parts);

    return this._cleanStatusText(rawStatus);
  }

  /**
   * Gets the total text length from text parts.
   */
  private _getTextLength(message: MyreUiMessage): number {
    const textParts = message.parts.filter(
      (p): p is { type: 'text'; text: string } => p.type === 'text',
    );

    return sumBy(textParts, p => p.text?.length || 0);
  }

  /**
   * Extracts the raw status message from data-status parts.
   */
  private _extractRawStatusFromParts(
    parts: readonly MyreUiMessagePart[],
  ): string | null {
    if (!parts || parts.length === 0) {
      return null;
    }

    const statusParts = parts.filter(
      (
        p,
      ): p is Extract<
        MyreUiMessagePart,
        { type: `${UIChunkType.DataStatus}` }
      > => isDataUIPart(p) && p.type === UIChunkType.DataStatus,
    );

    if (statusParts.length === 0) {
      return null;
    }

    const lastStatus = statusParts.at(-1);

    return lastStatus?.data.message ?? null;
  }

  /**
   * Cleans the status text by removing any XML residue.
   */
  private _cleanStatusText(rawStatus: string | null): string | null {
    if (!rawStatus) {
      return null;
    }

    return rawStatus.replace(/<[^>]*>/g, '').trim() || null;
  }
}
